//
// # Layout Instance Builder
//
// Factory for creating layout instances with proper defaults and validation.
// Handles all instance-specific domain logic including:
// - Name generation
// - Position calculation with offset
// - Generator resolution and content generation
// - Instance data structure building
//

import { Result, Context } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import type { DepotGet } from "Depot";
import type { LayoutChange } from "./layout";
import {
  createAbuttedSegments,
  updateAbuttedSegments,
  inferGridDimensions,
  computeGridDimensions,
  type ConstraintEdge,
} from "./abuttedArray";

// Parameter spec for generators (minimal interface to avoid circular deps)
export interface GeneratorParamSpec {
  name: string;
  default?: unknown;
}

// Generator spec returned by describe()
export interface GeneratorSpec {
  name: string;
  description: string;
  params: Array<GeneratorParamSpec>;
}

// Minimal generator interface (avoid circular dependencies)
export interface Generator {
  describe(): GeneratorSpec;
}

// Type for generator registry interface (avoid circular dependencies)
export interface GeneratorRegistry {
  resolve(
    ctx: Context,
    symbolName: string,
    pdkId?: string
  ): Generator | undefined;
  generateAndCache(
    ctx: Context,
    generatorPath: string[],
    params: Record<string, unknown>,
    stackId: schema.StackId,
    depot: DepotGet,
    pdkName?: string // Required for generic generators to load stack
  ): Promise<Result<schema.Ident>>;
  isFet(generatorPath: string[]): boolean;
  isExternal(generatorPath: string[]): boolean;
}

// Design state interface (minimal subset needed)
export interface LayoutDesignState {
  instances: {
    size(): number;
  };
  stackId?: schema.StackId;
}

// Array placement configuration.
// dx/dy are computed by the builder from the cell bbox snapped to grid.
export interface ArrayPlaceConfig {
  nrows: number;
  ncols: number;
}

export interface CreateInstanceOptions {
  ctx: Context;
  design: LayoutDesignState;
  symbolName: string;
  pdkId?: string;
  params?: Record<string, unknown>; // May include `m` for segmented placement
  position?: { x: number; y: number };
  name?: string;
  generatorRegistry: GeneratorRegistry;
  depot: DepotGet;
  namespace: string;
  // When provided, use this ident directly as the instance target instead of
  // resolving via generator registry. Used for instancing depot designs.
  targetIdent?: schema.Ident;
  // When provided, creates an ArrayPlace instead of scalar/segmented.
  // The target cell is generated once and arrayed uniformly.
  // dx/dy are computed from the cell bbox snapped to grid.
  arrayConfig?: ArrayPlaceConfig;
  // Arrange an m > 1 transistor as a near-square grid. Unset = straight line.
  rectangular?: boolean;
}

export interface CreateInstanceResult {
  change: LayoutChange;
  instanceName: string;
  // Constraint edges for the relative placer. Present when m > 1 and the
  // segments need outline-based placement after the add change is applied.
  constraintEdges?: Array<ConstraintEdge>;
}

export interface UpdateInstanceOptions {
  ctx: Context;
  instance: schema.layout.Instance;
  name?: string;
  params?: Record<string, unknown>;
  position?: { x: number; y: number };
  // Explicit placement to use. When provided, takes precedence over position
  // and the existing instance's place. Used to preserve placement changes
  // (e.g., flips) when regenerating due to params.
  place?: schema.layout.Place;
  generatorRegistry?: GeneratorRegistry;
  depot?: DepotGet; // Depot interface for content loading
  namespace?: string; // Required when depot is provided
  stackId?: schema.StackId;
  pdkId?: string; // Required for generic generators to load stack
  // Arrange an m > 1 transistor as a near-square grid. Unset means: keep the
  // shape the instance already has (inferred from its segments), falling back
  // to a straight line when there is nothing to infer from.
  rectangular?: boolean;
}

export interface UpdateInstanceResult {
  change: LayoutChange;
  // Constraint edges for the relative placer. Present when m > 1 and the
  // segments need outline-based placement after the update change is applied.
  constraintEdges?: Array<ConstraintEdge>;
}

export interface DeleteInstanceOptions {
  instanceId: number;
}

export interface DeleteInstanceResult {
  change: LayoutChange;
}

export class LayoutInstanceBuilder {
  // Default offset between instances when no position specified (in nanometers)
  private static readonly DEFAULT_OFFSET = 5000;

  /**
   * Create a new layout instance
   * Returns a Change object ready to be applied to the design
   */
  static async create(
    options: CreateInstanceOptions
  ): Promise<Result<CreateInstanceResult>> {
    const {
      ctx,
      design,
      symbolName,
      pdkId,
      params,
      position,
      name,
      generatorRegistry,
      depot,
      namespace,
      targetIdent: explicitTargetIdent,
      arrayConfig,
    } = options;

    // Extract multiplier 'm' from params (default 1 = scalar placement)
    // Multiplier stays in params and gets stored on the instance
    const m = typeof params?.m === "number" ? params.m : 1;

    // Generate instance name: use provided name, or derive from targetIdent's
    // last path segment, or fall back to symbolName-based naming.
    const instanceCount = design.instances.size();
    const derivedName =
      explicitTargetIdent?.kind === "hierpath"
        ? explicitTargetIdent.path[explicitTargetIdent.path.length - 1]
        : symbolName;
    const instanceName =
      name || this.generateInstanceName(derivedName, instanceCount);

    // Calculate position with offset if not provided
    const instancePosition =
      position || this.calculateDefaultPosition(instanceCount);

    // Resolve generator (only needed when no explicit target ident)
    const generatorResolution = explicitTargetIdent
      ? { generator: null, path: [] as string[], isPdkSpecific: false }
      : this.resolveGenerator(ctx, symbolName, pdkId, generatorRegistry);

    // Determine target identifier
    let targetIdent: schema.Ident;
    let instanceParams: Record<string, unknown> | undefined;

    if (explicitTargetIdent) {
      // Explicit target ident provided (depot design instancing) - skip generator resolution
      targetIdent = explicitTargetIdent;
    } else if (generatorResolution.generator) {
      // Generator found - generate content with provided params or defaults
      if (!design.stackId) {
        return Result.Err({
          why: "no-stack-id",
          message: "Layout must have a stackId for parametric instances",
        });
      }

      // Use the generator path based on where it was actually found
      const generatorPath = generatorResolution.path;

      // Apply default parameter values from generator spec
      const spec = generatorResolution.generator.describe();
      const generatorParams: Record<string, unknown> = { ...(params || {}) };
      for (const paramSpec of spec.params) {
        if (
          generatorParams[paramSpec.name] === undefined &&
          paramSpec.default !== undefined
        ) {
          generatorParams[paramSpec.name] = paramSpec.default;
        }
      }

      const generateResult = await generatorRegistry.generateAndCache(
        ctx,
        generatorPath,
        generatorParams,
        design.stackId,
        depot,
        pdkId // Pass pdkId for generic generators that need stack
      );

      if (generateResult.err) {
        return Result.Err({
          why: "generator-creation-failed",
          data: generateResult.val,
        });
      }

      targetIdent = generateResult.val;
      // Store only what the caller actually set, NOT the default-filled
      // `generatorParams` above. External (Custom Compiler) devices declare up
      // to 327 parameters, so storing the filled set wrote ~3.5KB (gf12/gf22)
      // to ~7KB (tsmc7) onto every placed instance, saved with the design and
      // synced over the CRDT — nearly all of it defaults nobody chose.
      //
      // Nothing needs the filled set from here. Generation already got it
      // (above), `generateAndCache` re-fills its own copy for the
      // content-addressed ident, `regenerateFromConfig` reads params out of
      // that ident rather than off the instance, and the OA exporter decodes
      // the same ident (see `exportDeviceParams` in NoaLib). Readers that show
      // a value to the user fall back to the generator spec's default when a
      // parameter isn't stored.
      instanceParams = params ? { ...params } : {};
    } else {
      // No generator found - use builtin symbol path
      targetIdent = {
        kind: "hierpath",
        path: ["symbols", symbolName],
      };
    }

    // Determine placement based on arrayConfig or multiplicity (m)
    let place: schema.layout.Place;

    if (arrayConfig) {
      // Array placement: uniform grid of the generated cell
      if (!pdkId || !design.stackId) {
        return Result.Err({
          why: "missing-pdk-or-stack",
          message: "pdkId and stackId are required for array placement",
        });
      }

      // Fetch cell bbox to compute grid-snapped dx/dy
      const contentResult = await depot.content.get(
        ctx,
        targetIdent,
        namespace
      );
      if (contentResult.err) {
        return Result.Err({
          why: "failed-to-fetch-layout-for-array",
          data: contentResult.val,
        });
      }
      const content = contentResult.val;
      if (content.contentKind !== "layout") {
        return Result.Err({
          why: "target-is-not-layout",
          message: `Expected layout content, got ${content.contentKind}`,
        });
      }

      const stackResult = await depot.stack.get(
        pdkId,
        design.stackId,
        namespace
      );
      if (stackResult.err) {
        return Result.Err({
          why: "failed-to-fetch-stack",
          data: stackResult.val,
        });
      }
      const [gridX, gridY] = stackResult.val.base;

      const cellWidth = content.abs.bbox.max.x - content.abs.bbox.min.x;
      const cellHeight = content.abs.bbox.max.y - content.abs.bbox.min.y;
      const dx = Math.ceil(cellWidth / gridX) * gridX;
      const dy = Math.ceil(cellHeight / gridY) * gridY;

      place = {
        kind: "array",
        array: {
          loc: { x: instancePosition.x, y: instancePosition.y },
          reflect: { horiz: false, vert: false },
          nrows: arrayConfig.nrows,
          ncols: arrayConfig.ncols,
          dx,
          dy,
        },
      };
    } else if (m === 1) {
      // Scalar placement
      place = {
        kind: "scalar",
        xform: {
          loc: { x: instancePosition.x, y: instancePosition.y },
          reflect: { horiz: false, vert: false },
        },
      };
    } else {
      // Segmented placement (m > 1)
      // Segments are created at placeholder positions. The caller invokes the
      // relative placer with the returned constraint edges to compute final
      // positions via outline-based abutment.
      if (!pdkId || !design.stackId) {
        return Result.Err({
          why: "missing-pdk-or-stack",
          message:
            "pdkId and stackId are required for multiplier segment placement",
        });
      }

      let constraintEdges: Array<ConstraintEdge>;

      if (generatorRegistry.isFet(generatorResolution.path)) {
        // Transistors get abutted segments with dummy-stripped variants
        const abuttedResult = await createAbuttedSegments({
          ctx,
          generatorPath: generatorResolution.path,
          baseParams: instanceParams || {},
          m,
          origin: instancePosition,
          pdkId,
          stackId: design.stackId,
          generatorRegistry,
          depot,
          instanceName,
          rectangular: options.rectangular,
        });

        if (abuttedResult.err) {
          return Result.Err(abuttedResult.val);
        }

        if (!abuttedResult.val) {
          return Result.Err({ why: "failed-to-create-abutted-segments" });
        }

        targetIdent = abuttedResult.val.defaultTarget;
        place = {
          kind: "segments",
          segments: abuttedResult.val.segments,
        };
        constraintEdges = abuttedResult.val.constraintEdges;
      } else {
        // Non-transistor: uniform segments at placeholder positions
        const uniform = this.buildUniformSegments(
          m,
          instancePosition,
          instanceName
        );
        place = { kind: "segments", segments: uniform.segments };
        constraintEdges = uniform.constraintEdges;
      }

      const change: LayoutChange = {
        contentKind: "layout",
        changeKind: "add",
        data: {
          targetKind: "instance" as const,
          name: instanceName,
          target: targetIdent,
          place,
          ...(instanceParams && { params: instanceParams }),
        },
      };

      return Result.Ok({ change, instanceName, constraintEdges });
    }

    // Scalar / array placement (no constraint edges needed)
    const instanceData = {
      targetKind: "instance" as const,
      name: instanceName,
      target: targetIdent,
      place,
      ...(instanceParams && { params: instanceParams }),
    };

    const change: LayoutChange = {
      contentKind: "layout",
      changeKind: "add",
      data: instanceData,
    };

    return Result.Ok({ change, instanceName });
  }

  /**
   * Update an existing layout instance
   * Returns a Change object ready to be applied to the design
   */
  static async update(
    options: UpdateInstanceOptions
  ): Promise<Result<UpdateInstanceResult>> {
    const {
      ctx,
      instance,
      name,
      params,
      position,
      place: explicitPlace,
      generatorRegistry,
      depot,
      namespace,
      stackId,
      pdkId,
    } = options;

    if (instance.id === undefined) {
      return Result.Err({
        why: "missing-instance-id",
        message: "Instance must have an ID to be updated",
      });
    }

    // Check if we need to regenerate due to parameter changes
    let targetIdent = instance.target;
    let updatedPlace: schema.layout.Place | undefined;
    let updatedConstraintEdges: Array<ConstraintEdge> | undefined;

    if (
      params !== undefined &&
      instance.target.kind === "hierpath" &&
      generatorRegistry &&
      depot &&
      namespace !== undefined &&
      stackId
    ) {
      // Check if this is a generator instance (direct, generated layout, or a
      // default-config external cell). The external-cell shape was missing
      // here: `generateAndCache` hands a Custom Compiler device placed on its
      // defaults a `cc_cell_<pdk>/<device>_prim_<stackId>` target, which fell
      // through to `generatorPath === undefined` — skipping regeneration while
      // the merge at the bottom of this function still wrote the new values, so
      // the instance claimed params its target's geometry did not have.
      const generatorPath = schema.generatorPathForTarget(instance.target);

      if (generatorPath) {
        // Merge delta params with existing instance params to get the full set.
        // The delta (params) overrides existing values; existing values fill in
        // anything the delta doesn't touch (e.g. ndum, nser, nfin when only
        // leftEndCap was changed).
        const mergedParams: Record<string, unknown> = {
          ...(instance.params || {}),
          ...params,
        };

        const m = typeof mergedParams.m === "number" ? mergedParams.m : 1;
        const origin = this.getInstanceOrigin(instance, position);

        // Step 1: Regenerate target content.
        // For abutted transistors this gets overridden below, but the
        // call is a cache hit (m is stripped from the cache key) so it's free.
        const generateResult = await generatorRegistry.generateAndCache(
          ctx,
          generatorPath,
          mergedParams,
          stackId,
          depot,
          pdkId
        );

        if (generateResult.err) {
          return Result.Err({
            why: "generator-regeneration-failed",
            data: generateResult.val,
          });
        }

        targetIdent = generateResult.val;

        // Step 2: Determine placement based on m.
        if (m === 1) {
          // Revert to scalar if previously segmented
          if (instance.place?.kind === "segments") {
            updatedPlace = {
              kind: "scalar",
              xform: {
                loc: origin,
                reflect: { horiz: false, vert: false },
              },
            };
          }
        } else if (pdkId) {
          // Segmented placement (m > 1).
          //
          // When m is unchanged and the instance was already segmented, we
          // refresh per-segment targets from the regenerated variants but
          // preserve each segment's xform -- users may have manually moved
          // segments, and a param edit shouldn't clobber those positions.
          //
          // When m changed or the instance was previously scalar, we have
          // no existing xforms to preserve and fall back to the create-time
          // rebuild. Editing m in layout is scheduled to be removed, so the
          // segment positions end up at placeholder in that case is an
          // accepted transitional behavior.
          const instanceName = name || instance.name;
          const existingSegments =
            instance.place?.kind === "segments" &&
            instance.place.segments.length === m
              ? instance.place.segments
              : undefined;

          // An explicit request wins; otherwise keep the shape the instance
          // already has. Re-deriving it from m alone would silently re-grid a
          // near-square instance into a row (or back), reassigning which
          // segments are edge cells while their positions stay put.
          const currentDims = inferGridDimensions(instance.place);
          const currentRectangular =
            currentDims === undefined ? undefined : currentDims.nrows > 1;
          const rectangular = options.rectangular ?? currentRectangular;

          // Refreshing targets in place only holds while the segments stay on
          // the grid the cells are derived for: it preserves every position and
          // rewrites only which cell each one points at. Compare the derived
          // dimensions against the ones the instance is actually laid out on,
          // not the flag — an instance built under an older arrangement rule
          // can agree about "is a grid" while disagreeing about the grid, and
          // refreshing it in place would hand edge cells to interior segments.
          const derivedDims = computeGridDimensions(m, rectangular);
          const shapeChanged =
            currentDims !== undefined &&
            (derivedDims.ncols !== currentDims.ncols ||
              derivedDims.nrows !== currentDims.nrows);

          if (generatorRegistry.isFet(generatorPath)) {
            if (existingSegments && !shapeChanged) {
              const updateResult = await updateAbuttedSegments({
                ctx,
                generatorPath,
                baseParams: mergedParams,
                m,
                pdkId,
                stackId,
                generatorRegistry,
                depot,
                existingSegments,
                rectangular,
              });

              if (updateResult.err) {
                return Result.Err(updateResult.val);
              }

              if (updateResult.val) {
                targetIdent = updateResult.val.defaultTarget;
                updatedPlace = {
                  kind: "segments",
                  segments: updateResult.val.segments,
                };
              }
            } else {
              const abuttedResult = await createAbuttedSegments({
                ctx,
                generatorPath,
                baseParams: mergedParams,
                m,
                origin,
                pdkId,
                stackId,
                generatorRegistry,
                depot,
                instanceName,
                rectangular,
              });

              if (abuttedResult.err) {
                return Result.Err(abuttedResult.val);
              }

              if (abuttedResult.val) {
                targetIdent = abuttedResult.val.defaultTarget;
                updatedPlace = {
                  kind: "segments",
                  segments: abuttedResult.val.segments,
                };
                updatedConstraintEdges = abuttedResult.val.constraintEdges;
              }
            }
          } else if (existingSegments) {
            // Non-transistor: segments share the instance-level target
            // (already regenerated above), so preserving xforms is enough.
            updatedPlace = { kind: "segments", segments: existingSegments };
          } else {
            // Non-transistor, structural change: uniform placeholder segments
            const uniform = this.buildUniformSegments(m, origin, instanceName);
            updatedPlace = { kind: "segments", segments: uniform.segments };
            updatedConstraintEdges = uniform.constraintEdges;
          }
        }
      }
    }

    // Get existing place for fallback
    const existingPlace =
      instance.place && instance.place.kind === "scalar"
        ? instance.place
        : {
            kind: "scalar" as const,
            xform: {
              loc: { x: 0, y: 0 },
              reflect: { horiz: false, vert: false },
            },
          };

    // Determine final placement
    // Priority: generator-computed updatedPlace > explicit place from caller > position-based > existing
    const finalPlace =
      updatedPlace ??
      explicitPlace ??
      (position
        ? {
            kind: "scalar" as const,
            xform: {
              loc: position,
              reflect: existingPlace.xform.reflect,
            },
          }
        : instance.place ?? existingPlace);

    // Build update data
    const updateData = {
      targetKind: "instance" as const,
      id: instance.id,
      name: name || instance.name,
      target: targetIdent,
      place: finalPlace,
      params:
        params !== undefined
          ? { ...(instance.params || {}), ...params }
          : instance.params,
    };

    // Create update change
    const change: LayoutChange = {
      contentKind: "layout",
      changeKind: "update",
      id: instance.id,
      data: updateData,
    };

    return Result.Ok({
      change,
      ...(updatedConstraintEdges && {
        constraintEdges: updatedConstraintEdges,
      }),
    });
  }

  /**
   * Delete an existing layout instance
   * Returns a Change object ready to be applied to the design
   */
  static delete(options: DeleteInstanceOptions): Result<DeleteInstanceResult> {
    const { instanceId } = options;

    // Create remove change
    const change: LayoutChange = {
      contentKind: "layout",
      changeKind: "remove",
      id: instanceId,
      data: {
        targetKind: "instance" as const,
      },
    };

    return Result.Ok({ change });
  }

  /**
   * Extract the origin position from an existing instance.
   * For segments, uses the first segment's location.
   * For scalar, uses the xform location.
   * Falls back to provided position or (0,0).
   */
  private static getInstanceOrigin(
    instance: schema.layout.Instance,
    position?: { x: number; y: number }
  ): { x: number; y: number } {
    if (position) return position;
    if (instance.place?.kind === "segments") {
      const first = instance.place.segments[0];
      if (first?.loc) return first.loc;
    }
    if (instance.place?.kind === "scalar") {
      return instance.place.xform.loc;
    }
    return { x: 0, y: 0 };
  }

  /**
   * Build segments at placeholder positions for non-transistor m > 1.
   * Unlike transistors, non-transistor segments don't need per-segment target
   * overrides — they all use the instance-level default target.
   * Returns segments and constraint edges for the relative placer.
   */
  // Public so that schematic→layout conversion can reuse the same uniform-tile
  // logic for non-transistor m>1 instances (parallel to createAbuttedSegments).
  static buildUniformSegments(
    m: number,
    origin: { x: number; y: number },
    instanceName: string
  ): {
    ncols: number;
    nrows: number;
    segments: Array<schema.layout.Segment>;
    constraintEdges: Array<ConstraintEdge>;
  } {
    const { ncols, nrows } = this.computeGridDimensions(m);
    const segments: Array<schema.layout.Segment> = [];
    const constraintEdges: Array<ConstraintEdge> = [];

    for (let row = 0; row < nrows; row++) {
      for (let col = 0; col < ncols; col++) {
        const segIndex = row * ncols + col;
        if (segIndex >= m) break;

        segments.push({
          loc: { x: origin.x, y: origin.y },
          reflect: { horiz: false, vert: false },
        });

        // Horizontal: this segment is Right of the previous column
        if (col > 0) {
          const prevSegIndex = row * ncols + (col - 1);
          constraintEdges.push({
            source_name: `${instanceName}.${segIndex}`,
            destination_name: `${instanceName}.${prevSegIndex}`,
            relation: {
              relation_type: "Right",
              space: 0,
              alignment: "Center",
            },
          });
        }

        // Vertical: this segment is Above the same column in the previous row
        if (row > 0) {
          const aboveSegIndex = (row - 1) * ncols + col;
          constraintEdges.push({
            source_name: `${instanceName}.${segIndex}`,
            destination_name: `${instanceName}.${aboveSegIndex}`,
            relation: {
              relation_type: "Above",
              space: 0,
              alignment: "Center",
            },
          });
        }
      }
    }

    return { ncols, nrows, segments, constraintEdges };
  }

  /**
   * Generate instance name from symbol name and count
   * Examples: "nmos" -> "N1", "res" -> "R1"
   */
  private static generateInstanceName(
    symbolName: string,
    count: number
  ): string {
    const firstChar = symbolName[0]?.toUpperCase() || "X";
    return `${firstChar}${count + 1}`;
  }

  /**
   * Calculate default position with diagonal offset to avoid overlaps
   */
  private static calculateDefaultPosition(instanceCount: number): {
    x: number;
    y: number;
  } {
    return {
      x: instanceCount * this.DEFAULT_OFFSET,
      y: instanceCount * this.DEFAULT_OFFSET,
    };
  }

  /**
   * Compute optimal grid dimensions for m segments.
   * Prioritizes: 1) Perfect square, 2) Rectangle with exact fit, 3) Near-square with partial last row
   */
  private static computeGridDimensions(m: number): {
    ncols: number;
    nrows: number;
  } {
    // 1. Check for perfect square
    const sqrt = Math.sqrt(m);
    if (Number.isInteger(sqrt)) {
      return { ncols: sqrt, nrows: sqrt };
    }

    // 2. Find best rectangular fit (factors of m)
    // Look for the factor pair closest to sqrt(m) for most square-like rectangle
    const sqrtFloor = Math.floor(sqrt);
    for (let i = sqrtFloor; i >= 2; i--) {
      if (m % i === 0) {
        // Found a factor pair: i x (m/i)
        // Use larger dimension as columns for wider layout
        return { ncols: m / i, nrows: i };
      }
    }

    // 3. No nice factors (m is prime) - use near-square with partial last row
    const ncols = Math.ceil(sqrt);
    const nrows = Math.ceil(m / ncols);
    return { ncols, nrows };
  }

  /**
   * Resolve generator - try PDK-specific first, then generic
   * Returns both the generator and the path to use for generation
   */
  private static resolveGenerator(
    ctx: Context,
    symbolName: string,
    pdkId: string | undefined,
    generatorRegistry: GeneratorRegistry
  ): { generator: Generator | null; path: string[]; isPdkSpecific: boolean } {
    // Try generic first to check if it exists
    const genericGenerator = generatorRegistry.resolve(ctx, symbolName);

    // Try PDK-specific if pdkId provided
    if (pdkId) {
      // resolve() with pdkId falls back to generic, so we need to check
      // if a PDK-specific one actually exists by comparing
      const resolvedGenerator = generatorRegistry.resolve(
        ctx,
        symbolName,
        pdkId
      );

      // Check if we got a PDK-specific generator (not the generic fallback)
      // by seeing if there's a difference when we don't pass pdkId
      if (resolvedGenerator && resolvedGenerator !== genericGenerator) {
        return {
          generator: resolvedGenerator,
          path: ["generators", pdkId, symbolName],
          isPdkSpecific: true,
        };
      }
    }

    // Use generic generator
    if (genericGenerator) {
      return {
        generator: genericGenerator,
        path: ["generators", symbolName],
        isPdkSpecific: false,
      };
    }

    // Not found
    return { generator: null, path: [], isPdkSpecific: false };
  }
}
